/* IndexedDB layer for the PWA's offline mode.
 *
 * Three concerns live here:
 *   1. `snapshot`  — the prefetched read-side data (calendar window of trips,
 *      trip details, chat history per trip, expenses per trip, CBP catalog,
 *      AIP MX hours, FAA NASR hours). Stored as one row keyed by 'current',
 *      containing the entire payload returned by GET /api/offline/snapshot.
 *   2. `outbox`    — write-side queue of mutations the user performed offline
 *      (currently: expense create + leg parameter edits). Each row carries
 *      enough info to replay the request when network returns.
 *   3. `meta`      — tiny key/value store for things like the current user's
 *      id (used by the SW to drop snapshots when a different user logs in),
 *      last-sync timestamps, etc.
 *
 * Used by: src/lib/offline-cache.js (read), src/lib/offline-outbox.js
 * (write), public/service-worker.js (write on push receipt).
 */
import { openDB } from "idb";

const DB_NAME = "altiv-offline";
const DB_VERSION = 1;

const STORE_SNAPSHOT = "snapshot";
const STORE_OUTBOX = "outbox";
const STORE_META = "meta";

let _dbPromise = null;

function getDB() {
  if (!_dbPromise) {
    _dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE_SNAPSHOT)) {
          db.createObjectStore(STORE_SNAPSHOT);
        }
        if (!db.objectStoreNames.contains(STORE_OUTBOX)) {
          // Auto-incrementing key so insertion order is preserved (FIFO replay).
          const s = db.createObjectStore(STORE_OUTBOX, {
            keyPath: "id",
            autoIncrement: true,
          });
          s.createIndex("by_status", "status");
        }
        if (!db.objectStoreNames.contains(STORE_META)) {
          db.createObjectStore(STORE_META);
        }
      },
    });
  }
  return _dbPromise;
}

// ─── Snapshot ──────────────────────────────────────────────────────────────

/** Replace the entire snapshot blob. Called by the SW after a successful
 * /api/offline/snapshot fetch. We keep one row keyed by 'current'; older
 * snapshots are not retained because the new one is always strictly fresher. */
export async function putSnapshot(payload) {
  const db = await getDB();
  await db.put(STORE_SNAPSHOT, payload, "current");
  await setMeta("last_snapshot_at", new Date().toISOString());
  await setMeta("last_snapshot_user_id", payload?.user_id || null);
}

export async function getSnapshot() {
  try {
    const db = await getDB();
    return await db.get(STORE_SNAPSHOT, "current");
  } catch {
    return null;
  }
}

export async function clearSnapshot() {
  const db = await getDB();
  await db.delete(STORE_SNAPSHOT, "current");
  await setMeta("last_snapshot_at", null);
  await setMeta("last_snapshot_user_id", null);
}

/** Drop every queued outbox entry — used on logout so the next user can't
 *  inadvertently replay the previous user's queued writes under their own
 *  bearer token (cross-tenant data contamination on shared devices). */
export async function clearOutbox() {
  const db = await getDB();
  await db.clear(STORE_OUTBOX);
}

// ─── Meta (key/value) ──────────────────────────────────────────────────────

export async function setMeta(key, value) {
  const db = await getDB();
  await db.put(STORE_META, value, key);
}

export async function getMeta(key) {
  try {
    const db = await getDB();
    return await db.get(STORE_META, key);
  } catch {
    return null;
  }
}

// ─── Outbox ────────────────────────────────────────────────────────────────

/** Queue a mutation to be replayed when network returns.
 * `entry` shape: { method, url, data, headers, kind, summary } */
export async function enqueueOutbox(entry) {
  const db = await getDB();
  const id = await db.add(STORE_OUTBOX, {
    ...entry,
    status: "pending",
    queued_at: new Date().toISOString(),
    attempts: 0,
  });
  return id;
}

export async function listOutbox() {
  try {
    const db = await getDB();
    return await db.getAll(STORE_OUTBOX);
  } catch {
    return [];
  }
}

export async function countPendingOutbox() {
  try {
    const db = await getDB();
    const tx = db.transaction(STORE_OUTBOX, "readonly");
    const idx = tx.store.index("by_status");
    return await idx.count("pending");
  } catch {
    return 0;
  }
}

export async function markOutboxResult(id, { status, error }) {
  const db = await getDB();
  const row = await db.get(STORE_OUTBOX, id);
  if (!row) return;
  row.status = status;
  row.last_error = error || null;
  row.attempts = (row.attempts || 0) + 1;
  row.last_attempt_at = new Date().toISOString();
  if (status === "sent") row.sent_at = row.last_attempt_at;
  await db.put(STORE_OUTBOX, row);
}

export async function deleteOutboxEntry(id) {
  const db = await getDB();
  await db.delete(STORE_OUTBOX, id);
}
